import React, { useEffect, useRef } from 'react';

/**
 * CareersStarfield — ambient canvas animation for the Careers hero.
 *
 * • 55 initial stars at random positions (sage 82% / champagne-gold 18%)
 *   slowly drifting, softly bouncing off edges, twinkling via per-star
 *   sine oscillation of opacity.
 * • Every 3–5.5s a new star "arrives": fades in over 1.2s while a
 *   champagne-gold ripple ring expands outward from its position over
 *   1.4s. The total star count is capped at 70 (oldest gets removed).
 * • Every 2.2–4.4s a temporary champagne-gold quadratic arc connects
 *   two random stars (control point lifted upward by 12% of horizontal
 *   distance). Arc fades in over 600ms, holds, then fades out over its
 *   final 900ms — lifetime 3500–5000ms.
 * • Canvas resizes on window resize without restarting animation state.
 * • DPR-aware for crisp rendering on high-density displays.
 */

const SAGE_RGB = '150,160,140';
const GOLD_RGB = '196,162,55';

const INITIAL_STARS = 55;
const MAX_STARS = 70;

const ARRIVAL_DURATION_MS = 1200;
const ARRIVAL_INTERVAL_MIN_MS = 3000;
const ARRIVAL_INTERVAL_MAX_MS = 5500;

const RIPPLE_DURATION_MS = 1400;
const RIPPLE_MAX_RADIUS = 30;
const RIPPLE_MAX_OPACITY = 0.3;

const ARC_INTERVAL_MIN_MS = 2200;
const ARC_INTERVAL_MAX_MS = 4400;
const ARC_LIFETIME_MIN_MS = 3500;
const ARC_LIFETIME_MAX_MS = 5000;
const ARC_FADE_IN_MS = 600;
const ARC_FADE_OUT_MS = 900;
const ARC_MAX_OPACITY = 0.35;
const ARC_LINE_WIDTH = 0.8;

const rand = (a, b) => a + Math.random() * (b - a);
const randSign = () => (Math.random() < 0.5 ? -1 : 1);

// Tiny champagne-gold pins hinting at global reach — standard global tech
// hub cities, positioned approximately over the world map background layer.
// Static placement, one-shot fade in/hold/fade out (see .careers-map-pin
// keyframes in index.css) — no continuous motion.
const OFFICE_PINS = [
  { city: 'San Francisco', left: '14%', top: '39%', delay: '0.2s' },
  { city: 'London', left: '50%', top: '33%', delay: '0.6s' },
  { city: 'Bengaluru', left: '73%', top: '49%', delay: '1s' },
  { city: 'Singapore', left: '80%', top: '54%', delay: '1.4s' },
  { city: 'São Paulo', left: '36%', top: '65%', delay: '1.8s' },
];

const makeStar = (arriving = false, nowMs = 0) => {
  const isGold = Math.random() < 0.18;
  return {
    x: rand(0.04, 0.96),
    y: rand(0.06, 0.94),
    vx: randSign() * 0.00006,
    vy: randSign() * 0.00006,
    r: rand(1.2, 3),
    baseOpacity: rand(0.25, 0.7),
    twinkleSpeed: rand(0.0003, 0.0009),
    twinklePhase: rand(0, Math.PI * 2),
    colorRgb: isGold ? GOLD_RGB : SAGE_RGB,
    arriving,
    arrivedAt: arriving ? nowMs : null,
  };
};

const CareersStarfield = () => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let W = 0;
    let H = 0;
    let dpr = 1;

    const stars = [];
    const ripples = []; // { x, y, startedAt }
    const arcs = [];   // { starA, starB, startedAt, lifetime }

    const startedAt = performance.now();

    for (let i = 0; i < INITIAL_STARS; i++) {
      stars.push(makeStar(false));
    }

    let lastArrivalAt = startedAt;
    let nextArrivalInterval = rand(ARRIVAL_INTERVAL_MIN_MS, ARRIVAL_INTERVAL_MAX_MS);
    let lastArcAt = startedAt;
    let nextArcInterval = rand(ARC_INTERVAL_MIN_MS, ARC_INTERVAL_MAX_MS);

    // Use Sets for O(1) "still exists" checks when validating arcs
    const isStillAStar = (s) => stars.indexOf(s) !== -1;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      dpr = window.devicePixelRatio || 1;
      W = Math.max(1, rect.width);
      H = Math.max(1, rect.height);
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Resize does NOT reset animation state — stars, ripples and arcs
      // keep their normalized positions and continue evolving.
    };

    const draw = (now) => {
      // ============== UPDATE ==============

      // Drift + soft edge bounce
      for (const s of stars) {
        s.x += s.vx;
        s.y += s.vy;
        if (s.x < 0.02 || s.x > 0.98) s.vx = -s.vx;
        if (s.y < 0.03 || s.y > 0.97) s.vy = -s.vy;
      }

      // Arriving stars: complete arrival when ramp window has elapsed
      for (const s of stars) {
        if (s.arriving && s.arrivedAt !== null && now - s.arrivedAt >= ARRIVAL_DURATION_MS) {
          s.arriving = false;
          s.arrivedAt = null;
        }
      }

      // Spawn an arriving star at the configured cadence
      if (now - lastArrivalAt >= nextArrivalInterval) {
        const newStar = makeStar(true, now);
        stars.push(newStar);
        ripples.push({ x: newStar.x, y: newStar.y, startedAt: now });
        if (stars.length > MAX_STARS) stars.shift(); // remove oldest
        lastArrivalAt = now;
        nextArrivalInterval = rand(ARRIVAL_INTERVAL_MIN_MS, ARRIVAL_INTERVAL_MAX_MS);
      }

      // Spawn an arc
      if (stars.length >= 2 && now - lastArcAt >= nextArcInterval) {
        const i1 = Math.floor(Math.random() * stars.length);
        let i2 = Math.floor(Math.random() * stars.length);
        if (i2 === i1) i2 = (i1 + 1) % stars.length;
        arcs.push({
          starA: stars[i1],
          starB: stars[i2],
          startedAt: now,
          lifetime: rand(ARC_LIFETIME_MIN_MS, ARC_LIFETIME_MAX_MS),
        });
        lastArcAt = now;
        nextArcInterval = rand(ARC_INTERVAL_MIN_MS, ARC_INTERVAL_MAX_MS);
      }

      // ============== DRAW ==============
      ctx.clearRect(0, 0, W, H);

      // ARCS first (behind everything)
      for (let i = arcs.length - 1; i >= 0; i--) {
        const a = arcs[i];
        const elapsed = now - a.startedAt;
        if (elapsed >= a.lifetime) {
          arcs.splice(i, 1);
          continue;
        }
        if (!isStillAStar(a.starA) || !isStillAStar(a.starB)) {
          arcs.splice(i, 1);
          continue;
        }
        // Opacity envelope: fade in 600ms → hold at ARC_MAX_OPACITY → fade out last 900ms
        let alpha;
        if (elapsed < ARC_FADE_IN_MS) {
          alpha = (elapsed / ARC_FADE_IN_MS) * ARC_MAX_OPACITY;
        } else if (elapsed > a.lifetime - ARC_FADE_OUT_MS) {
          const fadeT = (a.lifetime - elapsed) / ARC_FADE_OUT_MS;
          alpha = Math.max(0, fadeT) * ARC_MAX_OPACITY;
        } else {
          alpha = ARC_MAX_OPACITY;
        }
        const ax = a.starA.x * W;
        const ay = a.starA.y * H;
        const bx = a.starB.x * W;
        const by = a.starB.y * H;
        const dx = Math.abs(bx - ax);
        const cx = (ax + bx) / 2;
        const cy = (ay + by) / 2 - dx * 0.12;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.quadraticCurveTo(cx, cy, bx, by);
        ctx.strokeStyle = `rgba(${GOLD_RGB},${alpha})`;
        ctx.lineWidth = ARC_LINE_WIDTH;
        ctx.stroke();
      }

      // RIPPLES (behind stars but above arcs visually since they share gold tone)
      for (let i = ripples.length - 1; i >= 0; i--) {
        const r = ripples[i];
        const t = (now - r.startedAt) / RIPPLE_DURATION_MS;
        if (t >= 1) {
          ripples.splice(i, 1);
          continue;
        }
        const radius = t * RIPPLE_MAX_RADIUS;
        const alpha = (1 - t) * RIPPLE_MAX_OPACITY;
        ctx.beginPath();
        ctx.arc(r.x * W, r.y * H, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${GOLD_RGB},${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // STARS on top
      const elapsedTotal = now - startedAt; // for twinkle phase, monotonic
      for (const s of stars) {
        let opacity;
        if (s.arriving && s.arrivedAt !== null) {
          const t = Math.min(1, (now - s.arrivedAt) / ARRIVAL_DURATION_MS);
          opacity = t * s.baseOpacity;
        } else {
          opacity =
            s.baseOpacity +
            Math.sin(elapsedTotal * s.twinkleSpeed + s.twinklePhase) * s.baseOpacity * 0.5;
        }
        if (opacity <= 0.01) continue;

        const px = s.x * W;
        const py = s.y * H;
        // Outer soft glow
        ctx.fillStyle = `rgba(${s.colorRgb},${opacity * 0.12})`;
        ctx.beginPath();
        ctx.arc(px, py, s.r + 2, 0, Math.PI * 2);
        ctx.fill();
        // Inner solid
        ctx.fillStyle = `rgba(${s.colorRgb},${opacity})`;
        ctx.beginPath();
        ctx.arc(px, py, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = window.requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    rafRef.current = window.requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="careers-starfield"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      {/* Static world map texture layer — behind the animated canvas.
          Continent silhouettes at very low opacity so it reads as texture
          rather than a distinct graphic. Rendered via CSS mask so its
          color matches the sage/grey tone of the existing dots. A soft
          blur smooths out the country-boundary gaps so only the
          continent silhouettes remain. */}
      <div
        data-testid="careers-worldmap"
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundColor: 'rgb(150, 160, 140)',
          WebkitMaskImage: 'url(/world-map-outline.svg)',
          maskImage: 'url(/world-map-outline.svg)',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center center',
          maskPosition: 'center center',
          WebkitMaskSize: '105% auto',
          maskSize: '105% auto',
          opacity: 0.07,
          filter: 'blur(0.6px)',
        }}
      />
      {/* Office city pin markers — sit above the map texture, below the
          canvas star field. Fade in once, hold, fade out; then stay hidden. */}
      {OFFICE_PINS.map((pin) => (
        <span
          key={pin.city}
          data-testid={`careers-map-pin-${pin.city.toLowerCase().replace(/\s+/g, '-')}`}
          className="careers-map-pin absolute rounded-full"
          style={{
            left: pin.left,
            top: pin.top,
            width: '4px',
            height: '4px',
            transform: 'translate(-50%, -50%)',
            backgroundColor: `rgb(${GOLD_RGB})`,
            boxShadow: `0 0 6px 2px rgba(${GOLD_RGB},0.55)`,
            animationDelay: pin.delay,
          }}
        />
      ))}
      <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full" />
    </div>
  );
};

export default CareersStarfield;
